import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AmorphicService } from '../../../services/amorphic/amorphic.service';
import { AccountCenterService } from '../../../services/account-center/account-center.service';
import type { Policy } from '../../../../../common/js/customer/Policy';
import { CoverpathUtils } from '../../../../js/static/CoverpathUtils';
import { CommonUtils } from '../../../../../common/js/static/CommonUtils';
import { Utils } from '../../../../../common/js/Utils';
import { Controller as CoverpathClientController } from '../../../../../coverpath-client/public/js/Controller';
import type { CasApplication } from '../../../../classes/coverpath_subclasses/CasApplication';
import type { CoverpathBasePolicy } from '../../../../classes/coverpath_subclasses/CoverpathBasePolicy';

import { IsLoadingType, IsLoadingDataInterface } from '../../../interfaces/account-center/IsLoadingDataInterface';
import _ from 'lodash';
import { UICategory, UILoggerSingleton } from '../../../../../common/js/logger/UILoggerSingleton';

@Component({
  moduleId: module.id,
  selector: 'account-center',
  templateUrl: './account-center-detailed-view.component.html',
})
export class AccountCenterDetailedViewComponent implements OnInit {
  options: string[];
  isLoadingContext: IsLoadingDataInterface = {};
  workflowAction: string;

  // Maps workflowState:workflowSubState from the policy to a partial account center
  // page

  static workflowRoutes = [
    {
      names: [
        'Pre-application:Submitted',
        'Timeout:',
        'License-verification:Invalid',
        'License-verification:Fluidless Invalid',
        'License-verification:Verifying',
        'TLIC Application:Checking ID',
        'TLIC Application:Checking Insured ID',
        'TLIC Application:Checking Owner ID',
        'TLIC Application:Gathering Data',
        'Accelerated Application:Determine Underwriting Class',
        'TLIC Application:Initial Underwriting',
        'License-verification:Verifying Fluidless',
        'Send to Underwriting:Send to underwriting',
        'ESign:Resetting ESign Data',
        'Application Declined:With Vantage Option', //declined, rejected, cancelled
        'Application Declined:Uninsurable',
        'Application Rejected:ID Verification Failed',
        'Application Rejected:MVR Verification Failed',
        'Application Rejected:With Vantage Option',
        'Application Rejected:Uninsurable',
        'Application Canceled:Without TLIC',
        'Application Canceled By User:Without TLIC',
        'Application Canceled:No Labs',
        'Application Canceled:Not Accepted',
        'Application Canceled:Ran out of Time',
        'Funding Pause Canceled:Ran out of Time',
        'Terminated:Lapsed',
        'Terminated:Canceled - Free Look',
        'Terminated:Conversion_completed',
        'Terminated:Canceled',
        'Terminated:Not Taken',
        'Terminated:Rescinded',
        'Canceled:Canceled',
      ],
      partial: ['transition'],
    },
    {
      names: [
        'In Force:Free Look Period',
        'In Force:Grace Period',
        'In Force:Issued',
        'In Force:Pending Policy Docs',
        'Accelerated Application:Issuing Policy',
        'TLIC Issued:Final Underwriting',
        'TLIC Issued:Issuing Policy',
        'TLIC Issued:Waiting for Labs',
        'TLIC Application:Waiting for Acceptance',
        'TLIC Issued:Waiting for Policy Acceptance',
        'Application:Issuing Policy',
        'Haven Application:Waiting for Labs',
        'Haven Application:Waiting for Advisor Signature',
        'Manual Underwriting:Final Review',
        'Manual Underwriting:Awaiting APS',
        'Application:Final Review',
        'Application:Waiting for Issue Acceptance',
        'Advisor Amendment:Waiting for Advisor Amendment', //Amendment
        'Application Declined:Without TLIC',
        'Application Declined:With TLIC',
        'Application Rejected:With Haven Option',
        'Application Canceled By User:With TLIC',
        'Accelerated Application:Waiting for Acceptance',
        'Haven Application:Waiting for Application Acceptance',
        'Advisor Decline Review:Waiting for Advisor',
        'Customer Confirm Application:Waiting for customer signature',
        'ID Check:Verifying',
        'Application:Waiting for Annuity Acceptance',
        'Suitability:Reviewing',
        'Issuance:Processing',
        'Rejected:Suitability Rejected',
        'Funding Pause:Initiate Funds Transfer',
        'Funding Pause:Waiting for 1035 Funds',
        'Funding Pause:Funds have arrived',
        'Approved:Offer ready to send',
        'Conversion started:Conversion started',
        'Conversion is Ready:Conversion is Ready',
      ],
      partial: ['accountCenterTemplate'],
    },
    {
      names: ['TLIC Application:Determine Underwriting Class'],
      partial: function (policy: Policy) {
        const partial = ['transition'];

        if (policy.isAccelerated() && policy.application.isFrozen) {
          partial[0] = 'accountCenterTemplate';
        }

        return partial;
      },
    },
    {
      names: ['Underwriting:Underwriting'],
      partial: function (policy: Policy) {
        const partial = ['transition'];

        // Entering UW2 2nd time after AM4 we show accountCenterTemplate
        // Fluidless policies are un-signed at this stage so check for isAccelerated()
        // since rateClass is set after first UW2
        if (policy.applicationHasBeenSigned() || policy.isAccelerated()) {
          partial[0] = 'accountCenterTemplate';
        }

        return partial;
      },
    },
    {
      names: [
        'Accelerated Application:Waiting for Signature',
        'TLIC Application:Waiting for Signature',
        'Haven Application:Waiting for Signature',
      ],
      partial: function (policy: CoverpathBasePolicy) {
        const partial = ['accountCenterTemplate'];
        const metadata = policy.getOwnerSigningPerson().esignMetadata;
        const ownerHasSigned = (metadata && metadata.ownerSignComplete) || policy.applicationSignedDate;
        const waitingOnOtherSigners = metadata && metadata.waitingOnOtherSigners;
        const isEsignError = metadata && metadata.error; // if there is an error then got transition page
        if (
          CommonUtils.hasCoverpathProduct(policy) &&
          this.controller.isDocuSignRedirectEnabled &&
          ((!ownerHasSigned && !waitingOnOtherSigners) || isEsignError)
        ) {
          partial[0] = 'transition';
        }
        return partial;
      },
    },
    {
      names: [
        'TLIC Application:Correcting Insured SSN',
        'TLIC Application:Correcting Owner SSN',
        'TLIC Application:Correcting SSN',
      ],
      partial: 'idCheck',
    },
    {
      names: ['TLIC Application:Correcting License'],
      partial: 'mvr',
    },
    {
      names: ['TLIC Issued:Waiting For Amendment Signatures', 'Application:Waiting For Amendment Signatures'],
      partial: 'eSign',
    },
    {
      names: ['Operations Review:Operations Review'],
      partial: function (policy: Policy) {
        const partial = ['transition'];

        if (policy.isAnnuity()) {
          return partial;
        }

        if (CoverpathUtils.shouldShowAccountCenterTemplate(policy)) {
          partial[0] = 'accountCenterTemplate';
          return partial;
        }

        return partial;
      },
    },
  ];

  pageToInclude: string;
  policy: Policy;
  showPolicyDetails: boolean = false;
  isLoggedIn: boolean = false;
  isLoading = true;
  controller: CoverpathClientController;
  constructor(
    protected amorphicService: AmorphicService,
    protected router: Router,
    protected route: ActivatedRoute,
    protected accountCenterService: AccountCenterService
  ) {
    this.controller = amorphicService.controller as CoverpathClientController;
  }

  private workflowStateSubjectSubscription: Subscription;
  private routeSubscription: Subscription;
  async ngOnInit(): Promise<void> {
    UILoggerSingleton.getInstance().info(
      {
        module: 'AccountCenterDetailedViewComponent',
        function: 'ngOnInit',
        category: UICategory.request,
      },
      'Performance tracking - Navigated to detailed view'
    );

    this.isLoading = true;
    this.options = this.getMockWorkflowStates();
    let wasInsuredEsignReady = false;
    this.workflowStateSubjectSubscription = this.accountCenterService.workflowStateSubject.subscribe((action) => {
      if (action !== AccountCenterService.UNKNOWN_ACTION_STRING) {
        this.workflowAction = action;
        this.setWorkflowPage(action);
        this.accountCenterService.isLoadingSubject.next({ action: action });
      }
      const policy = Utils.get(this, 'accountCenterService.primaryPolicy');
      if (policy) {
        const app = policy.application as CasApplication;
        const person = policy.getOwnerSigningPerson();
        const metadata = person.esignMetadata;
        if (CoverpathUtils.isInDocusign(action) && metadata && metadata.linkValue && !metadata.ownerSignComplete) {
          app.docuSignLinkInProgress = false;
          window.location.href = person.esignMetadata.linkValue;
        }
        const insuredEsignRequired = CoverpathUtils.isInsuredSignRequiredAtFinalAcceptance(policy);
        if (!wasInsuredEsignReady && insuredEsignRequired) {
          wasInsuredEsignReady = true;
          this.accountCenterService.isLoadingSubject.next({ isLoading: false, action: 'InsuredEsignReady' });
        }
      }
    });

    this.accountCenterService.isLoadingSubject.subscribe((isLoadingData) => this.setIsLoading(isLoadingData));

    // @TODO remove once HL-21093 is done
    this.routeSubscription = this.route.queryParamMap
      .pipe(filter((params) => params.has('applicationNumber')))
      .subscribe(async (params) => {
        try {
          const fromOwnerSignComplete: string = params.get('owner-sign-complete') || undefined;
          await this.accountCenterService.setSelectedApplication(
            params.get('applicationNumber'),
            fromOwnerSignComplete
          );
          await this.accountCenterService.start();
        } catch (e) {
          const err = {
            name: e?.name,
            message: e?.message,
            stack: e?.stack,
            data: {
              ids: Utils.extractIds(this.amorphicService.controller.customer),
            },
          };
          UILoggerSingleton.getInstance().error(
            {
              module: 'AccountCenterDetailedViewComponent',
              function: 'ngOnInit',
              category: UICategory.request,
              error: err,
            },
            `Failed to show application detailed view for application number ${params.get('applicationNumber')}`
          );
          // @NOTE detail view depends on these two functions above,
          // so there is no reason to show this page if one of them fails
          await this.router.navigate(['account-center']);
        }
      });
  }

  ngOnDestroy(): void {
    if (this.workflowStateSubjectSubscription) {
      this.workflowStateSubjectSubscription.unsubscribe();
    }
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
    this.accountCenterService.actionLoopStop();
    this.accountCenterService.unsetSelectedApplication();
  }

  getMockWorkflowStates(): Array<string> {
    return _.flatten(AccountCenterDetailedViewComponent.workflowRoutes.map((items) => items.names)).sort();
  }

  setWorkflowPage(actionString: string): void {
    this.showPolicyDetails = actionString.startsWith('Pre-application');
    this.pageToInclude = this.getWorkflowPage(actionString);
  }

  /**
   * Does policy have a mappable workflow state and substate?
   */
  getWorkflowPage(actionString: string): string {
    let result;

    if (!result) {
      result = AccountCenterDetailedViewComponent.workflowRoutes
        .filter((value) => value.names.includes(actionString))
        .map((value) => value.partial)[0];
    }

    // default if no other state is set yet
    if (result) {
      if (typeof result === 'function') {
        return result.call(this, this.accountCenterService.primaryPolicy);
      } else {
        return result;
      }
    }

    return 'transition';
  }

  setIsLoading(isLoadingData: IsLoadingDataInterface): void {
    const noAction = !isLoadingData.action;
    const actionsMatch = this.isLoadingContext.action === isLoadingData.action;
    const isDuring = this.isLoadingContext.type === IsLoadingType.DURING;
    const isUntil = this.isLoadingContext.type === IsLoadingType.UNTIL;
    const newDuring = isLoadingData.type === IsLoadingType.DURING;
    const newUntil = isLoadingData.type === IsLoadingType.UNTIL;
    if (isLoadingData.isLoading === true) {
      this.isLoading = true;
      if (newDuring || newUntil) {
        this.isLoadingContext = isLoadingData;
      }
    } else {
      if (isDuring && (actionsMatch || noAction)) {
        return;
      }
      if (isUntil && (!actionsMatch || noAction)) {
        return;
      }
      this.isLoading = false;
      this.isLoadingContext = {};
    }
  }
}
